/**
 * GUESTS (docs/01 §6).
 *
 * "Today" here is the OFFICE day — 05:00 to 05:00 — so a guest who arrives at
 * 21:00 and leaves at 01:00 is one visit on one day, not two half-visits split
 * by midnight.
 *
 * Guests never authenticate and never self-check-in; arrival is logged by the
 * camera (Phase 8) or by whoever is hosting. This screen is therefore read-
 * mostly: who is expected, who has been, and whose birthday it is. Birthdays
 * apply to guests exactly as they do to owners.
 */

import {
  SpaceEmpty,
  SpaceStyles,
  formatAgo,
  formatBirthday,
  formatDayMonth,
  formatDuration,
  nameLookup,
  presenceAt,
  timeOfDay,
  todayKey,
  useNow,
  useSpace,
  type SpacePerson,
  type SpaceScreenProps,
  type VisitRow,
} from './spaceData.js'
import { addDays, rolloverDayBounds } from '@office/domain'
import type { Booking, DayKey, PersonId } from '@office/domain'

export interface GuestsProps extends SpaceScreenProps {
  readonly tickMs?: number
}

/** How far ahead the birthday list looks. */
export const BIRTHDAY_HORIZON_DAYS = 14

export function Guests(props: GuestsProps) {
  const { db, clock } = props
  const now = useNow(clock, props.tickMs ?? 60_000)
  const space = useSpace(db)
  const nameOf = nameLookup(space.people)

  const day = todayKey(now, space.settings.dayRolloverHour)
  const expected = guestBookingsOn(space.bookings, day, space.settings.dayRolloverHour)
  const birthdays = upcomingBirthdays(space.people, day, BIRTHDAY_HORIZON_DAYS)
  const visits = [...space.visits].sort((a, b) => b.arrivedAt - a.arrivedAt).slice(0, 20)

  // "Arriving mode ends when they are detected present, or at the booking's
  // end" (docs/01 §6). Without the first half, a guest who walked in forty
  // minutes ago is still announced as arriving for the whole booking.
  const presence = presenceAt(space.presenceEvents, now, space.settings.dayRolloverHour)
  const hasArrived = (b: Booking): boolean =>
    b.guestIds.some(id => presence.get(String(id) as PersonId)?.present === true)

  return (
    <section className="sp" aria-labelledby="sp-guests-h">
      <SpaceStyles />
      <div className="sp-head">
        <h2 className="sp-title" id="sp-guests-h">
          Guests
        </h2>
        <span className="sp-note">{formatDayMonth(now)}</span>
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Expected today
        </h3>
        {expected.length === 0 ? (
          <SpaceEmpty mark="👋" hint="An approved guest visit becomes a booking here, and the wall board switches to a welcome layout shortly before they arrive.">
            No guests booked today.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Guests expected today">
            {expected.map(booking => {
              const arrived = hasArrived(booking)
              const soon = isArrivingSoon(
                booking,
                now,
                space.settings.guestArrivingLeadMinutes,
                arrived,
              )
              return (
                <li key={booking.id} className="sp-row">
                  <span className="grow">
                    <span className="sp-person-name">
                      {booking.guestIds.length === 0
                        ? booking.title || 'Guest'
                        : booking.guestIds.map(id => nameOf(String(id))).join(', ')}
                    </span>
                    <span className="sp-person-meta">
                      {timeOfDay(booking.startsAt)}–{timeOfDay(booking.endsAt)}
                      {booking.title === '' ? '' : ` · ${booking.title}`}
                    </span>
                  </span>
                  {soon ? (
                    <span className="pill">Arriving soon</span>
                  ) : arrived ? (
                    <span className="pill pill-ok">Here</span>
                  ) : null}
                </li>
              )
            })}
          </ul>
        )}
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Birthdays
        </h3>
        {birthdays.length === 0 ? (
          <SpaceEmpty mark="🎂" hint="Birthdays cover guests as well as owners, so add one when you register somebody.">
            None in the next {BIRTHDAY_HORIZON_DAYS} days.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Upcoming birthdays">
            {birthdays.map(({ person, inDays }) => (
              <li key={person.id} className="sp-row">
                <span className="grow">
                  <span className="sp-person-name">{person.displayName}</span>
                  <span className="sp-person-meta">
                    {person.birthday === null ? '' : formatBirthday(person.birthday)}
                    {person.company === null ? '' : ` · ${person.company}`}
                  </span>
                </span>
                <span className={inDays === 0 ? 'pill pill-ok' : 'pill'}>
                  {inDays === 0 ? 'Today' : `in ${inDays}d`}
                </span>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="card">
        <h3 className="sp-card-title">
          Guest log
        </h3>
        {visits.length === 0 ? (
          <SpaceEmpty mark="🗓" hint="Every visit is kept with its date, duration and host, which turns into a relationship history for free.">
            No visits logged yet.
          </SpaceEmpty>
        ) : (
          <ul className="sp-list sp-rows" aria-label="Guest log">
            {visits.map(visit => (
              <li key={visit.id} className="sp-row">
                <span className="grow">
                  <span className="sp-person-name">{nameOf(visit.guestId)}</span>
                  <span className="sp-person-meta">
                    {formatDayMonth(visit.arrivedAt)} · {visitLength(visit, now)}
                    {visit.hostMemberId === null ? '' : ` · host ${nameOf(visit.hostMemberId)}`}
                  </span>
                </span>
                <span className="sp-note">{formatAgo(visit.arrivedAt, now)}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

// ---------------------------------------------------------------------------

/** Confirmed guest bookings inside one office day, across every resource. */
export function guestBookingsOn(
  bookings: readonly Booking[],
  day: DayKey,
  rolloverHour: number,
): Booking[] {
  const { startUtc, endUtc } = rolloverDayBounds(day, rolloverHour)
  return bookings
    .filter(
      b =>
        b.kind === 'guest' &&
        b.status === 'confirmed' &&
        b.endsAt > b.startsAt &&
        b.startsAt < endUtc &&
        startUtc < b.endsAt,
    )
    .sort((a, b) => a.startsAt - b.startsAt || (a.id < b.id ? -1 : 1))
}

/**
 * Arriving mode's lead time, applied to a single booking (docs/01 §6).
 *
 * The window opens `leadMinutes` before the booking and closes at the booking's
 * end **or the moment the guest is detected present**, whichever comes first —
 * both halves of the rule, not just the timer.
 */
export function isArrivingSoon(
  booking: Booking,
  now: number,
  leadMinutes: number,
  arrived = false,
): boolean {
  if (arrived) return false
  const lead = Math.max(0, leadMinutes) * 60_000
  return now >= booking.startsAt - lead && now < booking.endsAt
}

export interface UpcomingBirthday {
  readonly person: SpacePerson
  readonly inDays: number
}

/**
 * Birthdays from today forward, keyed on 'MM-DD' so the year is never needed
 * and the turn of the year is not a special case.
 */
export function upcomingBirthdays(
  people: readonly SpacePerson[],
  today: DayKey,
  horizonDays: number,
): UpcomingBirthday[] {
  const out: UpcomingBirthday[] = []
  for (let offset = 0; offset <= horizonDays; offset++) {
    const key = addDays(today, offset).slice(5)
    for (const person of people) {
      if (person.birthday === key) out.push({ person, inDays: offset })
    }
  }
  return out
}

function visitLength(visit: VisitRow, now: number): string {
  if (visit.departedAt === null) return `still here · ${formatDuration(now - visit.arrivedAt)}`
  return formatDuration(visit.departedAt - visit.arrivedAt)
}
